import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Card, CardBody, Row, Col, Label } from "reactstrap";
import ControlledRowTextField from "../../../../components/ControlledRowTextField";
import ButtonComponent from "../../../../components/ButtonComponent";
import Select from "react-select";
import { Button, CardHeader, Table } from "react-bootstrap";
import {
  MenuTypeForMenu,
  dineTypeForMenu,
} from "../../../../dummyData/dummyData";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faTrash } from "@fortawesome/free-solid-svg-icons";
import BreadCrumbs from "../../../../components/BreadCrumbs";
import {
  useCreateMenuMutation,
  useLazyGetMenuItemsListQuery,
} from "../../../../redux/services/MenusService";
import TimeRangeSelector from "../../../../components/TimeRangeSelector";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { customFilterOption } from "../../../../utility/Utility";
import { useSelector } from "react-redux";
import { getImageUrl } from "../../../../dummyData/FoodAttributes";
import { brandOptions, CUSTOM_MENU, MENU_SUPPORTED_APPS, SET_MENU } from "../../../../constants/constans";
import TimeSelector from "../../../../components/TimeSelector";

interface FormData {
  displayName: string;
  servingStartTime: string;
  servingEndTime: string;
  maxDaysToScheduleAhead: number;
  orderClosingTime: string;
}

interface DineOptionType {
  value: string;
  label: string;
}

interface ItemOptionType {
  displayName: string;
  tinyId: string;
  price: number;
  itemId: string;
  imageUrl: string;
  itemType: string;
  isSellingProduct?: boolean;
  allergenInformation?: string[];
  foodAttributes?: string[];
  overridePrice?: number;
}

export interface MenuTypeOption {
  value: string;
  label: string;
}

const AddMenus: React.FC = () => {
  const navigate = useNavigate();

  const restaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();

  const [selectedOption, setSelectedOption] = useState<ItemOptionType | null>(
    null
  );
  const [overridePrice, setOverridePrice] = useState<string>("");
  const [selectedMenuTypeOption, setSelectedMenuTypeOption] =
    useState<MenuTypeOption | null>(null);
  const [selectedItems, setSelectedItems] = useState<ItemOptionType[]>([]);
  const [selectedDineType, setSelectedDineType] =
    useState<DineOptionType | null>(null);
  const [selectedBrandType, setSelectedBrandType] = useState<{ value: string; label: string } | null>(null);
  const [brandTypeError, setBrandTypeError] = useState<string | null>(null);
  const [selectedSupportedApps, setSelectedSupportedApps] = useState<{ value: string; label: string }[]>([]);
  const [supportedAppsError, setSupportedAppsError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [itemError, setItemError] = useState<string | null>(null);
  const [dineTypeError, setDineTypeError] = useState<string | null>(null);
  const [menuItemsOptionsForMenu, setMenuItemsOptionsForMenu] = useState([]);

  const [trigger, { isLoading }] = useLazyGetMenuItemsListQuery();
  const [createMenu] = useCreateMenuMutation();

  const onSubmit = async (data: FormData) => {
    if (!selectedBrandType) {
      setBrandTypeError("Brand must be selected");
      return;
    }
    if (!selectedDineType) {
      setDineTypeError("Dine type must be selected");
      return;
    }
    if (selectedItems.length === 0) {
      setItemError("At least one menu item must be selected");
      return;
    }
    if (!selectedSupportedApps || selectedSupportedApps.length === 0) {
      setSupportedAppsError("At least one supported app must be selected");
      return;
    }
    setIsSaving(true);
    setItemError(null);
    setDineTypeError(null);
    setSupportedAppsError(null);

    const menuData = {
      restaurantId: restaurantId,
      displayName: data.displayName,
      brand: selectedBrandType?.value as "Curry King" | "Wok & Flame" | "Rasa Bath",
      dineType: selectedDineType?.value ?? "",
      servingTime: `${data.servingStartTime || "08:30"}-${data.servingEndTime || "20:30"
        }`,
      orderClosingTime: data.orderClosingTime,
      maxDaysToScheduleAhead: +data.maxDaysToScheduleAhead,
      supportedApps: selectedSupportedApps.map(app => app.value),
      items: selectedItems.map((item) => {
        let trimmedItemType = item.itemType;
        if (
          trimmedItemType === SET_MENU ||
          trimmedItemType === CUSTOM_MENU
        ) {
          trimmedItemType = trimmedItemType.replace(" ", "");
        }
        const itemData: any = {
          displayName: item.displayName,
          tinyId: item.tinyId,
          price: item.price,
          itemId: item.itemId,
          itemType: trimmedItemType,
          allergenInformation: item.allergenInformation || [],
          foodAttributes: item.foodAttributes || [],
        };
        if (item.overridePrice !== undefined) {
          itemData.overridePrice = item.overridePrice;
        }
        return itemData;
      }),
    };

    try {
      await createMenu(menuData).unwrap();
      toast.success("Menu Created Successfully");
      navigate("/menu/menu-list");
    } catch (error: any) {
      if (error.status === 409) {
        toast.error("This menu name already used");
        return;
      }
      toast.error(error);
      console.error("Failed to create menu:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAdd = () => {
    if (selectedOption) {
      const itemToAdd = {
        ...selectedOption,
        ...(overridePrice && { overridePrice: parseFloat(overridePrice) }),
      };
      setSelectedItems([...selectedItems, itemToAdd]);
      setSelectedOption(null);
      setOverridePrice("");
      setItemError(null);
    }
  };

  const handleDelete = (index: number) => {
    const updatedItems = [...selectedItems];
    updatedItems.splice(index, 1);
    setSelectedItems(updatedItems);
  };

  const handleDineTypeChange = (option: DineOptionType | null) => {
    setSelectedDineType(option);

    if (option) {
      setDineTypeError(null);
    }
  };

  const handleBrandChange = (option: { value: string; label: string } | null) => {
    setSelectedBrandType(option);
    if (option) {
      setBrandTypeError(null);
    }
  };

  const handleSupportedAppsChange = (options: readonly { value: string; label: string }[]) => {
    setSelectedSupportedApps([...options]);
    if (options && options.length > 0) {
      setSupportedAppsError(null);
    }
  };

  const handleMenuTypeChange = (option: MenuTypeOption | null) => {
    setSelectedMenuTypeOption(option);
    if (option) {
      setItemError(null);
    }
  };

  const handleItemChange = (option: ItemOptionType | null) => {
    setSelectedOption(option);
    if (option) {
      setItemError(null);
      setOverridePrice("");
    }
  };

  useEffect(() => {
    if (selectedMenuTypeOption) {
      trigger({ restaurantId, menuType: selectedMenuTypeOption.value })
        .unwrap()
        .then((MenuItems) => {
          const menuItems = MenuItems.map((item: ItemOptionType) => {
            let trimmedItemType = item.itemType;
            if (
              trimmedItemType === SET_MENU ||
              trimmedItemType === CUSTOM_MENU
            ) {
              trimmedItemType = trimmedItemType.replace(" ", "");
            }
            return {
              value: item.itemId,
              label: (
                <div
                  style={{ display: "flex", justifyContent: "left", gap: "5%" }}
                >
                  <img
                    src={item.imageUrl}
                    alt={item.displayName}
                    width="30px"
                    height="30px"
                  />
                  {item.displayName}
                  {item.price && <span className="ml-2">Rs.{item.price.toFixed(2)}</span>}
                </div>
              ),
              displayName: item.displayName,
              tinyId: item.tinyId,
              price: item.price,
              itemId: item.itemId,
              itemType: trimmedItemType,
              imageUrl: item.imageUrl,
              isSellingProduct: item.isSellingProduct,
              allergenInformation: item.allergenInformation || [],
              foodAttributes: item.foodAttributes || [],
            };
          });
          setMenuItemsOptionsForMenu(menuItems);
        })
        .catch((error) => {
          console.error("Failed to fetch menu items:", error);
        });
    }
  }, [selectedMenuTypeOption, trigger, restaurantId]);

  // Filter out already selected items from the dropdown options
  const isMenuItemTypeSelected = selectedMenuTypeOption?.value === "Single";
  const availableMenuItemsOptions = menuItemsOptionsForMenu.filter(
    (item: any) =>
      !selectedItems.some(
        (selectedItem: any) => (selectedItem.itemId === item.itemId)
      ) &&
      (!isMenuItemTypeSelected || item.isSellingProduct === true)
  );

  return (
    <>
      <BreadCrumbs hasSubMenus={true} />
      <br />
      <Card>
        <CardHeader style={{ backgroundColor: "#fff" }}>
          <h4>Add Menu</h4>
        </CardHeader>
        <CardBody>
          <form
            className="auth-login-form mt-2"
            onSubmit={handleSubmit(onSubmit)}
          >
            <ControlledRowTextField
              label="Menu Name *"
              inputName="displayName"
              inputType="text"
              placeholder="Enter Menu Name"
              control={control}
              rules={{ required: "Menu Name is Required" }}
            />
            {errors.displayName && (
              <p className="text-danger">
                {errors.displayName.message as string}
              </p>
            )}
            <hr />
            <Row className="align-items-center">
              <Col lg="4">
                <Label className="pt-1 form-label">Brand *</Label>
              </Col>
              <Col lg="8">
                <Select
                  value={selectedBrandType}
                  onChange={handleBrandChange}
                  options={brandOptions}
                  placeholder="Select Brand"
                  isSearchable
                  required
                />
              </Col>
            </Row>
            {brandTypeError && (
              <Row className="mt-2">
                <Col>
                  <div className="text-danger">{brandTypeError}</div>
                </Col>
              </Row>
            )}
            <hr />
            <TimeRangeSelector
              label="Serving Time *"
              inputName={["servingStartTime", "servingEndTime"]}
              inputType="time"
              placeholder={["Start Time", "End Time"]}
              control={control}
              defaultValue={["08:30", "20:30"]}
              rules={{ required: "Serving Time is Required" }}
            />
            {(errors.servingEndTime || errors.servingStartTime) && (
              <p className="text-danger">
                {errors.servingEndTime?.message as string}
              </p>
            )}
            <hr />
            <TimeSelector
              label="Ordering Closes At *"
              inputName="orderClosingTime"
              inputType="time"
              placeholder="Select time"
              control={control}
              rules={{ required: "Time is required" }}
              errors={errors}
              isRequired={true}
              hasSideLabel={false}
              defaultValue="10:30"
            />
            {(errors.orderClosingTime) && (
              <p className="text-danger">
                {errors.orderClosingTime?.message as string}
              </p>
            )}
            <hr />
            <ControlledRowTextField
              label="Max Days to Schedule Orders Ahead *"
              inputName="maxDaysToScheduleAhead"
              inputType="number"
              placeholder="Enter Max Days to Schedule Orders Ahead"
              control={control}
              rules={{ required: "Max Days to Schedule Orders Ahead" }}
            />
            {errors.maxDaysToScheduleAhead && (
              <p className="text-danger">
                {errors.maxDaysToScheduleAhead.message as string}
              </p>
            )}
            <hr />
            <Row className="align-items-center">
              <Col lg="4">
                <Label className="pt-1 form-label">Dine Type *</Label>
              </Col>
              <Col lg="8">
                <Select
                  value={selectedDineType}
                  onChange={handleDineTypeChange}
                  options={dineTypeForMenu}
                  placeholder="Select Dine Type"
                  isSearchable
                />
              </Col>
            </Row>
            {dineTypeError && (
              <Row className="mt-2">
                <Col>
                  <div className="text-danger">{dineTypeError}</div>
                </Col>
              </Row>
            )}
            <hr />
            <Row className="align-items-center">
              <Col lg="4">
                <Label className="pt-1 form-label">Supported Apps *</Label>
              </Col>
              <Col lg="8">
                <Select
                  value={selectedSupportedApps}
                  onChange={handleSupportedAppsChange}
                  options={MENU_SUPPORTED_APPS}
                  placeholder="Select Channel"
                  isSearchable
                  isMulti={true}
                />
              </Col>
            </Row>
            {supportedAppsError && (
              <Row className="mt-2">
                <Col>
                  <div className="text-danger">{supportedAppsError}</div>
                </Col>
              </Row>
            )}
            <hr />
          </form>
        </CardBody>
      </Card>

      <Card className="mt-3">
        <CardBody>
          <Row className="align-items-center mt-3">
            <Col sm={3}>
              <Label className="pt-1 form-label">Menu Items</Label>
            </Col>
            <Col sm={2}>
              <Select
                value={selectedMenuTypeOption}
                onChange={handleMenuTypeChange}
                options={MenuTypeForMenu}
                placeholder="Menu Type"
                isSearchable
              />
            </Col>
            <Col sm={3}>
              <Select
                value={selectedOption}
                onChange={handleItemChange}
                options={availableMenuItemsOptions}
                placeholder={isLoading ? "Loading..." : "Search menu item"}
                isDisabled={!selectedMenuTypeOption}
                loadingMessage={() => "Loading menu items..."}
                noOptionsMessage={() =>
                  isLoading ? "Loading..." : "No items found"
                }
                filterOption={customFilterOption}
              />
            </Col>
            <Col sm={2}>
              <input
                type="number"
                className="form-control"
                placeholder="Override Price"
                value={overridePrice}
                onChange={(e) => setOverridePrice(e.target.value)}
                disabled={!selectedOption}
                step="0.01"
                min="0"
              />
            </Col>
            <Col sm={2} className="text-right">
              <ButtonComponent
                titel="+Add Item"
                type="button"
                onClick={handleAdd}
                isDisabled={!selectedMenuTypeOption || !selectedOption}
              />
            </Col>
          </Row>
          {itemError && (
            <Row className="mt-2">
              <Col>
                <div className="text-danger">{itemError}</div>
              </Col>
            </Row>
          )}
          <hr />
          <h5>Selected Items</h5>
          <Table bordered className="mt-3" responsive>
            <thead>
              <tr>
                <th>Name</th>
                <th>Tiny Id</th>
                <th>Price (Rs.)</th>
                <th>Override Price (Rs.)</th>
                <th>Food Attributes</th>
                <th>Allergen Information</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {selectedItems.map((item, index) => (
                <tr key={index}>
                  <td>
                    <div className="d-flex gap-3">{item.displayName}</div>
                  </td>
                  <td>{item?.tinyId}</td>
                  <td>{item.price}</td>
                  <td>{item.overridePrice || "-"}</td>
                  <td>
                    <div className="d-flex gap-2">
                      {item.foodAttributes?.length ? (
                        item.foodAttributes.map((attribute, i) => (
                          <img
                            key={i}
                            src={getImageUrl(attribute)}
                            alt={attribute}
                            width="24"
                            height="24"
                            title={attribute}
                          />
                        ))
                      ) : (
                        <span>None</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      {item.allergenInformation?.length ? (
                        item.allergenInformation.map((attribute, i) => (
                          <img
                            key={i}
                            src={getImageUrl(attribute)}
                            alt={attribute}
                            width="24"
                            height="24"
                            title={attribute}
                          />
                        ))
                      ) : (
                        <span>None</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <Button variant="light" onClick={() => handleDelete(index)}>
                      <FontAwesomeIcon icon={faTrash} color="#F24E1E" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="d-flex justify-content-end mt-4 mb-2">
            {isSaving ? (
              <ButtonComponent
                icon={<FontAwesomeIcon icon={faSpinner} spin />}
                type="submit"
                titel="Saving..."
                isDisabled={true}
              />
            ) : (
              <ButtonComponent
                type="submit"
                titel="Save"
                onClick={handleSubmit(onSubmit)}
              />
            )}
          </div>
        </CardBody>
      </Card>
    </>
  );
};

export default AddMenus;
